import { ReactNode, useState } from 'react'
import { isNil } from 'lodash'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'

import { DATE_FORMATS } from '@/utils/dateHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { useDocumentUpload } from '@/components/documents/upload/hooks/useDocumentUpload'
import { UploadDocumentType } from '@/constants/businessConstants'
import { getUserDocumentsByCategoryInternalName } from '@/features/user/your-practice/documents/lib/userDocuments.selector'
import {
  selectUserDocumentCategory,
  selectUserDocumentCategoryByInternalName,
} from '@/featuresV1/Admin/UserDocumentCategories/userDocumentCategories.selectors'
import { selectCurrentAnnualTaxYear } from '@/featuresV1/Admin/AnnualTaxDetails/annualTaxDetails.selector'
import { TypographyStyles, TextColors } from '@/styles'
import { Button, DocumentUploadDialog, Link, Popup, Text } from '@/ui'

import { DatePicker } from '@/base'

interface IPriorYearTaxReturnDocUploadDialog {
  label: string
  value?: string | number | null
  popupContent?: ReactNode
  documentCat?: string
  documentType?: UploadDocumentType
  taxYear?: string
}

export const PriorYearTaxReturnDocUploadDialog = ({
  label,
  value,
  popupContent,
  documentCat,
  documentType = UploadDocumentType.TAX,
  taxYear,
}: IPriorYearTaxReturnDocUploadDialog) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [year, setYear] = useState(taxYear?.toString())
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)
  const nextTaxYear = (Number(currentTaxYear) + 1).toString()

  const taxDocCat = useReselector(
    selectUserDocumentCategoryByInternalName,
    documentCat
  )

  const userDocs = useReselector(
    getUserDocumentsByCategoryInternalName,
    documentCat
  )

  const sentCategory = useReselector(selectUserDocumentCategory, taxDocCat?.id)

  const { uploadDocuments, isUploading } = useDocumentUpload({
    documentCategory: documentCat || '',
    documentType: documentType || UploadDocumentType.TAX,
    year: taxYear,
    onUploadSuccess: () => {
      setIsDialogOpen(false)
    },
    onUploadError: (errorMessage) => {
      console.error('Upload failed:', errorMessage)
    },
    additionalTrackingProperties: {
      document_subcategory: taxDocCat?.id.toString() || '',
      document_name: sentCategory?.title || '',
      tax_year: Number(taxYear),
    },
  })

  const handleDocumentsUpload = async (documents: File[]) => {
    await uploadDocuments(documents)
  }

  return (
    <div className="flex flex-row text-left gap-2 mb-4 items-start">
      <Text className="w-full md:w-3/10" variant={TypographyStyles.LABEL_SM}>
        {label}
        {popupContent && <Popup content={popupContent} />}
      </Text>

      <div className="w-full md:w-7/10">
        {!isNil(value) && (
          <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
            {value}
          </Text>
        )}

        {userDocs.map((doc) =>
          doc?.signedUrl ? (
            <Link
              key={doc.id}
              href={doc.signedUrl}
              variant="primary"
              color={TextColors.MUTED}
            >
              {doc?.fileDescription}
            </Link>
          ) : (
            <Text
              key={doc.id}
              variant={TypographyStyles.BODY_SM}
              color={TextColors.MUTED}
            >
              {doc?.fileDescription}
            </Text>
          )
        )}

        {taxDocCat && !userDocs.length && (
          <Button
            variant="secondary"
            onClick={() => setIsDialogOpen(true)}
            iconLeft={regular('upload')}
            loading={isUploading}
            size="sm"
          >
            Upload
          </Button>
        )}
      </div>

      <DocumentUploadDialog
        cardTitle="Final Tax Return"
        cardDescription="This is your Federal and/or State Tax Return(s)."
        isOpen={isDialogOpen}
        onUpload={handleDocumentsUpload}
        onClose={() => setIsDialogOpen(false)}
        classNameBody="overflow-visible"
      >
        <DatePicker
          placeholder="Select year"
          label="Tax Year"
          value={year?.toString() ?? ''}
          maxDate={new Date()}
          onChange={setYear}
          dateFormat={DATE_FORMATS.YEAR}
          showYearPicker
          clearable
          afterLabel={
            <Popup
              content={
                <Text>
                  A tax year is the 12-month calendar year covered by a tax
                  return. In the U.S., the tax year for individuals runs from
                  Jan. 1 to Dec. 31 and includes taxes owed on earnings during
                  that period. For example, taxes withheld or owed for earnings
                  during the calendar year {currentTaxYear} would be included on
                  the tax return sent to the IRS in {nextTaxYear}. Even though
                  you are sending the tax return in {nextTaxYear}, it is for the
                  tax year {currentTaxYear}.
                </Text>
              }
            />
          }
        />
      </DocumentUploadDialog>
    </div>
  )
}
